import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, TrendingUp, Wallet, Users, Calendar, Trophy } from "lucide-react";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(v || 0);
const pct = (v) => `${(v ?? 0).toFixed(1)}%`;

const MONATE = [
  { value: "alle", label: "Alle Daten" },
  ...Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    return { value: ym, label };
  }),
];

export const StandortVergleich = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monat, setMonat] = useState("alle");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = monat !== "alle" ? { monat } : {};
      const res = await axios.get(`${API}/standorte/vergleich`, { params, withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [monat]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="p-8 text-center text-slate-500">Laden...</div>;
  if (!data) return null;

  // Top-Performer ermitteln
  const items = data.items || [];
  const withUmsatz = items.filter((i) => i.umsatz_brutto > 0);
  const top = withUmsatz.length > 0
    ? withUmsatz.reduce((a, b) => (a.profit > b.profit ? a : b))
    : null;
  const maxUmsatz = Math.max(...items.map((i) => i.umsatz_brutto || 0), 1);

  return (
    <div data-testid="standort-vergleich-page">
      <header className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="page-title">Standort-Vergleich</h1>
            <p className="page-subtitle">Konsolidierte Auswertung aller Filialen mit Side-by-Side-Vergleich</p>
          </div>
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-slate-500" />
            <Label className="text-xs whitespace-nowrap">Zeitraum:</Label>
            <Select value={monat} onValueChange={setMonat}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="select-zeitraum">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONATE.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* Konsolidierte Top-Zeile */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KPI icon={<TrendingUp size={14} />} label="Umsatz gesamt" value={fmt(data.konsolidiert.umsatz_brutto)} />
        <KPI icon={<Users size={14} />} label="Mitarbeiter" value={data.konsolidiert.mitarbeiter_count} />
        <KPI icon={<Wallet size={14} />} label="Personalkosten" value={fmt(data.konsolidiert.personalkosten)} />
        <KPI
          icon={<Trophy size={14} />}
          label="Gewinn (Rohmarge)"
          value={`${fmt(data.konsolidiert.profit)} (${pct(data.konsolidiert.profit_marge_prozent)})`}
          highlight={data.konsolidiert.profit >= 0 ? "positive" : "negative"}
        />
      </div>

      {/* Top-Performer Hinweis */}
      {top && items.length > 1 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-sm p-3 mb-6 flex items-center gap-2">
          <Trophy size={16} className="text-amber-600 dark:text-amber-400" />
          <span className="text-sm">
            <b>Top-Performer:</b> <span className="text-[#3A4D39] dark:text-violet-300 font-semibold">{top.name}</span>
            {" "}mit {fmt(top.profit)} Gewinn ({pct(top.profit_marge_prozent)} Marge)
          </span>
        </div>
      )}

      {/* Standorte Side-by-Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="standorte-cards">
        {items.map((s, idx) => (
          <div
            key={s.id || `none-${idx}`}
            className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-sm p-5"
            style={{ borderLeftWidth: 4, borderLeftColor: s.farbe }}
            data-testid={`standort-vergleich-${s.id || 'none'}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-slate-500" />
                  <h3 className="font-display text-sm font-bold text-[#3A4D39] dark:text-violet-300">{s.name}</h3>
                  {s.ist_haupt && (
                    <span className="text-[9px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">Haupt</span>
                  )}
                </div>
                {s.stadt && <p className="text-xs text-slate-500 mt-0.5">{s.stadt}</p>}
              </div>
            </div>

            {/* Umsatz-Balken (visuell) */}
            <div className="mb-4">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-xs text-slate-500">Umsatz</span>
                <span className="font-mono text-sm font-bold">{fmt(s.umsatz_brutto)}</span>
              </div>
              <div className="h-2 bg-stone-100 dark:bg-slate-900 rounded overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${(s.umsatz_brutto / maxUmsatz) * 100}%`,
                    background: s.farbe,
                  }}
                />
              </div>
              {s.verkaufstage > 0 && (
                <p className="text-[10px] text-slate-400 mt-1">
                  Ø {fmt(s.umsatz_avg_tag)} an {s.verkaufstage} Tagen
                </p>
              )}
            </div>

            {/* Kennzahlen-Grid */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <Stat label="Speisen" value={`${s.speisen_count} (${pct(s.speisen_marge_prozent)})`} />
              <Stat label="Getränke" value={`${s.getraenke_count} (${pct(s.getraenke_marge_prozent)})`} />
              <Stat label="Mitarbeiter" value={s.mitarbeiter_count} />
              <Stat label="Personalkosten" value={fmt(s.personalkosten)} />
              <Stat label="Fixkosten" value={fmt(s.fixkosten)} />
              <Stat
                label="Gewinn"
                value={`${fmt(s.profit)} (${pct(s.profit_marge_prozent)})`}
                color={s.profit >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const KPI = ({ icon, label, value, highlight }) => (
  <div className="bg-white dark:bg-slate-800 border border-stone-200 dark:border-slate-700 rounded-sm p-3">
    <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
      {icon} {label}
    </div>
    <p className={`font-mono text-base font-bold ${
      highlight === "positive" ? "text-emerald-700 dark:text-emerald-400"
        : highlight === "negative" ? "text-red-600 dark:text-red-400"
        : "text-[#3A4D39] dark:text-violet-300"
    }`}>{value}</p>
  </div>
);

const Stat = ({ label, value, color }) => (
  <div>
    <div className="text-slate-500 text-[10px] uppercase tracking-wide">{label}</div>
    <div className={`font-mono ${color || ""}`}>{value}</div>
  </div>
);

export default StandortVergleich;
